import { forwardRef, Ref } from "react";

import { cx } from "@/utils";
import Clickable from "@components/Clickable";

import styles from "./Button.module.css";

export type ButtonProps = {
  className?: string;
  icon?: IconComponent | null;
  disabled?: boolean;
  iconPlacement?: "before" | "after";
  block?: boolean;
  iconProps?: IconComponentProps;
  size?: "sm" | "md" | "lg";
  variant?: "primary" | "secondary" | "tertiary";
  iconRotated?: boolean;
} & React.ComponentPropsWithoutRef<"button" | "a">;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      className,
      icon: Icon = null,
      iconPlacement = "before",
      block,
      children,
      size = "md",
      variant = "primary",
      iconRotated = false,
      iconProps = {},
      ...props
    },
    ref
  ) => {
    return (
      <Clickable
        {...props}
        ref={ref as unknown as Ref<HTMLAnchorElement & HTMLButtonElement>}
        className={cx(
          styles.root,
          {
            // variants
            [styles.primary]: variant === "primary",
            [styles.secondary]: variant === "secondary",
            [styles.tertiary]: variant === "tertiary",

            //sizes
            [styles.sm]: size === "sm",
            [styles.md]: size === "md",
            [styles.lg]: size === "lg",
            [styles.block]: block,
            [styles.hasBeforeIcon]:
              iconPlacement === "before" && Icon && children,
            [styles.hasAfterIcon]:
              iconPlacement === "after" && Icon && children,
            [styles.iconWithoutContent]: Icon && !children,
          },
          className
        )}
      >
        {iconPlacement === "before" && Icon ? (
          <Icon className={styles.icon} {...iconProps} />
        ) : null}
        {children && <span>{children}</span>}
        {iconPlacement === "after" && Icon ? (
          <Icon
            className={cx(styles.icon, { [styles.rotated]: iconRotated })}
            {...iconProps}
          />
        ) : null}
      </Clickable>
    );
  }
);

Button.displayName = "Button";

export default Button;
